import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat, PF } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { WishlistItemType } from 'data/e-commerce/products';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { getWishlistData } from 'api/user/_requests';
import { useDispatch } from 'react-redux';
import { removeFromWishlist } from 'api/user/_requests';
import { removeFromWishlist as removeFromWishlistAction } from 'redux-state/user/wishlistSlice';
import { toast } from 'react-hot-toast';
import { addToCart } from 'api/cart/_requests';
import { addProductsToVendor as addToCartAction } from 'redux-state/user/cartSlice';

const EcomWishlistTable = () => {
  const dispatch = useDispatch();
  const [wishlistData, setWishlistData] = useState([]);

  const fetchWishlistData = async () => {
    const response = await getWishlistData();

    const temp = response.map((item: WishlistItemType) => {
      return {
        ...item,
        thumbnail:
          PF + (item.thumbnail !== undefined ? item.thumbnail : 'goods')
      };
    });

    setWishlistData(temp);
  };

  const handleRemoveFromWishlist = async (
    productId: string,
    variantId: string | null
  ) => {
    await removeFromWishlist({ productId, variantId })
      .then(() => {
        dispatch(removeFromWishlistAction({ productId, variantId }));
        const temp = wishlistData.filter(
          (item: WishlistItemType) =>
            item.productId !== productId || item.variantId !== variantId
        );
        setWishlistData(temp);
        toast.success('Removed from wishlist');
      })
      .catch(err => {
        toast.error('Failed to remove from wishlist');
      });
  };

  const handleAddToCart = async (product: WishlistItemType) => {
    const data = {
      vendorId: product.vendorId,
      product: {
        productId: product.productId,
        variantId: product.variantId || '',
        price: product.discountedPrice,
        quantity: 1
      }
    };
    await addToCart(data)
      .then(res => {
        dispatch(
          addToCartAction({
            vendorId: product.vendorId,
            products: {
              productId: product.productId,
              variantId: product.variantId || '',
              price: product.discountedPrice,
              quantity: 1,
              name: product.name,
              thumbnail: product.thumbnail,
              vendorId: product.vendorId,
              discountedPrice: product.discountedPrice,
              color: product.color || '',
              size: product.size || '',
              weight: product.weight || ''
            },
            totalAmount: product.discountedPrice,
            totalAmountAfterDiscount: 0
          })
        );
        toast.success('Added to cart');
      })
      .catch(err => {
        toast.error('Failed to add to cart');
      });
  };

  const columns: ColumnDef<WishlistItemType>[] = [
    {
      id: 'productImage',
      accessorKey: '',
      cell: ({ row: { original } }) => {
        const { thumbnail } = original;
        return (
          <div className="rounded-2 border border-translucent d-inline-block">
            <img
              src={thumbnail}
              alt="product"
              width={53}
              className="image-square"
            />
          </div>
        );
      },
      meta: {
        cellProps: { className: 'py-0' },
        headerProps: { style: { width: '7%' } }
      }
    },
    {
      accessorKey: 'product',
      header: 'Products',
      cell: ({ row: { original } }) => {
        const { name } = original;
        return (
          <>
            <Link
              to={`/product-details/${original.productId}`}
              className="fw-semibold line-clamp-1"
            >
              {name}
            </Link>
            <span className="text-body-tertiary fw-semibold d-block text-uppercase">
              {original.color &&
                original.color !== '' &&
                `Color: ${original.color}`}
              {original.size &&
                original.size !== '' &&
                ` | Size: ${original.size}`}
              {original.weight &&
                original.weight !== '' &&
                ` | Weight: ${original.weight}`}
            </span>
          </>
        );
      },
      meta: {
        headerProps: { style: { minWidth: 250, width: '30%' } },
        cellProps: { className: 'pe-11' }
      }
    },
    // {
    //   accessorKey: 'color',
    //   header: 'Color',
    //   meta: {
    //     headerProps: { style: { width: '16%' } },
    //     cellProps: { className: 'white-space-nowrap' }
    //   }
    // },
    // {
    //   accessorKey: 'size',
    //   header: 'Size',
    //   meta: {
    //     headerProps: { style: { width: '10%' } },
    //     cellProps: { className: 'text-body-tertiary fw-semibold' }
    //   }
    // },
    {
      accessorKey: 'price',
      header: () => 'Price',
      cell: ({ row: { original } }) =>
        currencyFormat(original.discountedPrice, {
          currency: 'SAR',
          style: 'currency'
        }),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end fw-semibold' }
      }
    },
    {
      id: 'action',
      cell: ({ row: { original } }) => {
        return (
          <div className="d-flex gap-2 justify-content-end">
            <Button
              size="sm"
              className="text-body-quaternary text-body-tertiary-hover"
              onClick={() =>
                handleRemoveFromWishlist(original.productId, original.variantId)
              }
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
            <Button
              variant="primary"
              className="fs-10 text-nowrap"
              startIcon={<FontAwesomeIcon icon={faShoppingCart} />}
              onClick={() => handleAddToCart(original)}
            >
              Add to cart
            </Button>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '35%' } }
      }
    }
  ];

  useEffect(() => {
    fetchWishlistData();
  }, []);

  const table = useAdvanceTable({
    data: wishlistData,
    columns,
    pageSize: 5,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default EcomWishlistTable;
